import React from "react";
import BreadcrumbsComponent from "../../../components/_shared/Breadcrumbs";
import Classes from "../../../components/skills-hub/[categoryId]/Classes";
import { useRouter } from "next/router";
import AppContainer from "../../../layouts/AppContainer";
import { Category, LiveClassParent } from "../../../interfaces";
import { buildCategorySchema } from "../../../lib/categorySchema";
import { getBookableSkillsHub } from "../../../lib/skillsHub";
import { SKILLS_HUB_OPEN } from "../../../constants/skillsHub";
import Meta from "../../../layouts/Meta";
import { GetStaticPaths, GetStaticProps } from "next";
import { Box, Grid, Skeleton } from "@mui/material";

const CategoryList = (props: Props) => {
  const { category, classes } = props;
  const router = useRouter();
  const { asPath, query } = router;
  const { categoryId }: any = query;
  // categoryId took time to reload. use asPath in the meantime
  const url = categoryId ? `/skills-hub/${categoryId}` : asPath;

  const Header = () => <h1 className="title">{category?.lcCategoryName}</h1>;

  // Course ItemList JSON-LD, built server-side so it lands in the crawlable HTML.
  const categorySchema =
    classes && classes.length ? buildCategorySchema(category, classes) : null;

  // fallback: true pages render before getStaticProps resolves on first hit.
  if (router.isFallback) {
    return (
      <AppContainer>
        <Grid container spacing={2}>
          {[1, 2, 3, 4, 5, 6].map((item) => (
            <Grid item xs={6} key={item}>
              <Box p={2}>
                <Skeleton variant="rectangular" width={"100%"} height={"100px"} />
              </Box>
            </Grid>
          ))}
        </Grid>
      </AppContainer>
    );
  }

  return (
    <AppContainer>
      <Meta
        title={category?.lcCategoryName}
        description={category?.lcCategoryDesc}
        keywords={category?.lcCategoryName}
        image={category?.lcCategoryImage}
      />
      {categorySchema && (
        <script
          type="application/ld+json"
          // Escape `<` so a "</script>" inside CMS-sourced fields cannot break out.
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(categorySchema).replace(/</g, "\\u003c"),
          }}
        />
      )}
      <BreadcrumbsComponent url={url} />
      <Header />
      <Classes classes={classes} />
    </AppContainer>
  );
};

// Only bookable categories build (lib/skillsHub.ts): a published category with
// no upcoming class 404s rather than answering "No Classes Found" on a 200.
// While Skills Hub is closed nothing builds, and next.config.js redirects the
// URL to /skills-hub before it reaches this page.
export const getStaticProps: GetStaticProps = async ({ params }) => {
  const { categoryId }: any = params;

  if (!SKILLS_HUB_OPEN) return { notFound: true };

  const entry = (await getBookableSkillsHub()).find(
    (bookable) => bookable.category.lcCategoryId === categoryId,
  );
  if (!entry) {
    return { notFound: true, revalidate: 60 };
  }

  return {
    props: { category: entry.category, classes: entry.classes },
    revalidate: 60,
  };
};

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const bookable = SKILLS_HUB_OPEN ? await getBookableSkillsHub() : [];
  const paths: any[] = [];

  locales?.forEach((locale) => {
    bookable.forEach(({ category }) =>
      paths.push({ params: { categoryId: category.lcCategoryId }, locale })
    );
  });

  // "blocking", not true: fallback: true answers an unknown slug with a 200
  // shell before getStaticProps runs, so notFound would only apply on the
  // second request.
  return { paths, fallback: "blocking" };
};

interface Props {
  category: Category;
  classes: LiveClassParent[];
}

export default CategoryList;
